import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useT } from "../lib/i18n";
import { ShieldCheck, Heart, CreditCard } from "@phosphor-icons/react";

/**
 * Live trust-badge tiles — extracted from Landing.jsx so the new TrustStrip
 * wrapper can reuse it with a dark theme. Renders nothing until the backend
 * confirms there's enough signal to publish (gated by sample-size floors,
 * see `/api/public/trust-badges`).
 */
export default function TrustBadge({ theme = "light", standalone = true }) {
    const t = useT();
    const [data, setData] = useState(null);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        api.get("/public/trust-badges")
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoaded(true));
    }, []);

    if (!loaded || !data?.enough_data) return null;

    const tiles = [];
    if (data.confirmation_rate_pct !== null) {
        tiles.push({
            key: "confirmed",
            Icon: ShieldCheck,
            headline: `${data.confirmation_rate_pct}%`,
            label: t("trust_badge.confirmed_label"),
            sub: t("trust_badge.confirmed_sub", { n: data.decided_count }),
        });
    }
    if (data.thank_yous_30d >= 5) {
        tiles.push({
            key: "thanks",
            Icon: Heart,
            headline: data.thank_yous_30d.toLocaleString(),
            label: t("trust_badge.thanks_label"),
            sub: t("trust_badge.thanks_sub"),
        });
    }
    if (data.cards_funded_30d >= 10) {
        tiles.push({
            key: "cards",
            Icon: CreditCard,
            headline: data.cards_funded_30d.toLocaleString(),
            label: t("trust_badge.cards_label"),
            sub: t("trust_badge.cards_sub"),
        });
    }
    if (tiles.length === 0) return null;

    const dark = theme === "dark";
    const eyebrow = dark ? "text-white/70" : "text-[#4A5C50]";
    const h2 = dark ? "text-[#FBF9F6]" : "text-[#112318]";
    const sub = dark ? "text-white/60" : "text-[#4A5C50]";
    const tileBg = dark
        ? "bg-white/5 border-white/10 backdrop-blur-md"
        : "bg-white border-[#E5E0D8]";
    const tileLabel = dark ? "text-[#FBF9F6]" : "text-[#112318]";
    const tileSub = dark ? "text-white/55" : "text-[#4A5C50]";
    const headlineColor = dark ? "text-[#CBA35C]" : "text-[#184A30]";
    const iconColor = dark ? "text-[#CBA35C]" : "text-[#184A30]";

    const inner = (
        <>
            {standalone && (
                <div className="text-center max-w-2xl mx-auto">
                    <p className={`text-[11px] tracking-[0.3em] uppercase ${eyebrow}`}>
                        {t("trust_badge.eyebrow")}
                    </p>
                    <h2 className={`serif text-3xl md:text-4xl mt-3 ${h2}`}>
                        {t("trust_badge.title")}
                    </h2>
                    <p className={`text-sm mt-2 ${sub}`}>{t("trust_badge.subtitle")}</p>
                </div>
            )}
            <div
                className={`${standalone ? "mt-10" : ""} grid gap-6 ${
                    tiles.length === 1 ? "max-w-md mx-auto" :
                    tiles.length === 2 ? "md:grid-cols-2 max-w-3xl mx-auto" :
                    "md:grid-cols-3"
                }`}
            >
                {tiles.map(({ key, Icon, headline, label, sub: tSub }) => (
                    <div
                        key={key}
                        className={`rounded-2xl ${tileBg} border p-7 text-center`}
                        data-testid={`trust-tile-${key}`}
                    >
                        <Icon size={28} weight="duotone" className={`mx-auto ${iconColor}`} />
                        <p className={`serif text-5xl ${headlineColor} mt-3`} data-testid={`trust-headline-${key}`}>
                            {headline}
                        </p>
                        <p className={`text-sm ${tileLabel} mt-2`}>{label}</p>
                        <p className={`text-[11px] ${tileSub} mt-2`}>{tSub}</p>
                    </div>
                ))}
            </div>
        </>
    );

    if (!standalone) return inner;

    return (
        <section
            className={`border-t ${dark ? "border-white/10 bg-[#0A1F16]" : "border-[#E5E0D8] bg-[#FBF9F6]"}`}
            data-testid="landing-trust-badge"
        >
            <div className="max-w-7xl mx-auto px-6 py-16 md:py-20">{inner}</div>
        </section>
    );
}
